// @owners { team: patients-team }
import { useCheckoutCourierTipping } from '~shared/features/checkout/hooks/useCheckoutCourierTipping';
import { getOrder } from '~shared/features/checkout/selectors/getOrder';
import { useOrderPricing } from '~shared/queries';
import { useSelectorShared } from '~shared/store';

type Results = {
  fsa: string;
  label: string;
  description: string;
};

export const useDeliveryFeeCopy = (isApplePayPaymentMethod?: boolean): Results => {
  const { isEligibleForTipping } = useCheckoutCourierTipping();
  const { hasAppliedDeliveryFee, isPaymentMethodRequired } = useOrderPricing();
  const order = useSelectorShared(getOrder);
  const hasTipAmount = order.tip_amount !== undefined && order.tip_amount > 0;
  const hasTip = isEligibleForTipping && hasTipAmount;

  // only show medication payment one if patient paying with apple pay
  if (isApplePayPaymentMethod && isPaymentMethodRequired && (hasTip || hasAppliedDeliveryFee)) {
    return {
      label: 'Medication and other payment',
      fsa: 'Depending on your plan, tips & delivery fees may not be eligible expenses.',
      description:
        'Total amount will be paid with Apple Pay. If paying with an FSA/HSA/Carrot card, depending on your plan, tips and delivery fees may not be qualified expenses.',
    };
  }

  if (hasTip && hasAppliedDeliveryFee) {
    return {
      label: 'Tip & delivery payment',
      fsa: 'Depending on your plan, tips & delivery fees may not be eligible expenses. You can change your payment method here to pay for tip & delivery.',
      description:
        'If paying with an FSA/HSA/Carrot card, depending on your plan, tips & delivery fees may not be a qualified expense.',
    };
  }
  if (hasAppliedDeliveryFee) {
    return {
      label: 'Delivery payment',
      fsa: 'Depending on your plan, delivery fees may not be eligible expenses. You can change your payment method here to pay for delivery.',
      description:
        'If paying with an FSA/HSA/Carrot card, depending on your plan, delivery fees may not be a qualified expense.',
    };
  }
  return {
    label: 'Tip payment',
    fsa: 'Depending on your plan, tips may not be eligible expenses. You can change your payment method here to pay for tips.',
    description: 'If paying with an FSA/HSA/Carrot card, depending on your plan, tips may not be a qualified expense.',
  };
};
